// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what is worth finding, and how it is asked for
//  ---------------------------------------------------------------------------
//  The same split as md-engine-sql.ts, for the same reason: every decision in
//  the search story is a pure function over plain data here, and nothing here
//  executes anything. The worker and the tests then run the identical strings
//  against the identical rules.
//
//  THERE HAS NEVER BEEN AN APP-WIDE SEARCH. md-database.jsx:1408 lowercases
//  every cell of every row of ONE database into a haystack — 170,000 cells on
//  the 5k account — and can only find a row of the table already open. Nothing
//  finds a day, a record, a project, a board, or the database next door.
//
//  `docs` is every document on the account in one table, so an index over it is
//  app-wide by construction rather than by anyone remembering to add a family.
//
//  WHY THE EXTRACTION IS GENERIC AND NOT PER-FAMILY. Twenty-five shape-aware
//  extractors are twenty-five things to edit when a family is added, and the
//  one somebody forgets is silently unsearchable — which is the failure C2 was
//  designed against, arriving from the other direction. So the rule below is
//  about JSON and not about ManDana: harvest the string leaves, skip the field
//  names that are machinery. A family nobody has invented yet is searchable the
//  day it is first written, and over-harvesting costs a little ranking noise
//  rather than a document that cannot be found.
//
//  WHY FTS5 STORES ITS OWN COPY rather than reading `docs` directly. External
//  content over `docs` would index the raw JSON: every brace, every id, and the
//  word "title" in every document on the account. `search` below holds the
//  harvested text instead — which for a board scene or a database row is a
//  small fraction of the JSON — and search_fts is external-content over THAT.
//  One copy of the words, and snippet() has something to quote from.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  /* Machinery, not writing. Showing any of these in a search box would read as
     a bug rather than as completeness — and `md:record-index` in particular is
     a catalogue of every record on the account, so it would match nearly
     everything. Prefixes and exact keys, the same two shapes md-record-index.ts
     already ignores, and for the same reasons. */
  const SKIP_KEYS = new Set<string>([
    'md:record-index', 'md:navigation', 'md:syncMeta', 'md:syncRev',
    'md:syncRevKey', 'md:syncTouched',
    'md:boardOpen', 'md:viewOffset', 'md:sidebarCollapsed',
    'md:theme', 'md:lang', 'md:calc',
  ]);

  /* `boardfile:` is a base64 image and `tombstones:` is a list of things that
     are gone. `crdt:` is the merge state that rides beside a document — the
     document's history, encoded, and the whole reason it rides *beside* the
     JSON is that the JSON stays searchable. Left in, every state is a result
     titled with its own base64. None of the three is words. */
  const SKIP_PREFIXES = ['md:boardfile:', 'md:tombstones:', 'md:crdt:'];

  /* Field names whose values are addresses, clocks or style — never anything a
     person typed. Matched case-insensitively, plus any name ENDING in Id/Ids/At
     so a family that invents `pursuitId` is covered without an edit here.

     This list decides ranking quality, not correctness: a machinery field left
     out adds noise, and a content field wrongly listed loses words. So it is
     short, and it only holds names whose meaning is fixed across the app. */
  const SKIP_FIELDS = new Set<string>([
    /* `v` is deliberately NOT here. It reads like "version" and on an edge it
       is the database's CELLS for a record — the words a person filled a table
       in with. Numbers are not indexed at all, so a numeric `v` costs nothing
       either way, and the ambiguous name is worth keeping for the content. */
    'id', 'ids', 'key', 'keys', 'rev', 'revision', 'version', 'seed',
    'created', 'updated', 'createdat', 'updatedat', 'at', 'time', 'date', 'day',
    'by', 'createdby', 'updatedby', 'author', 'hash', 'fp', 'checksum',
    'color', 'colour', 'strokecolor', 'backgroundcolor', 'fontfamily', 'fill',
    'type', 'kind', 'mode', 'status', 'state', 'icon', 'glyph', 'emoji',
    'x', 'y', 'w', 'h', 'width', 'height', 'angle', 'opacity', 'index', 'order',
    'font', 'align', 'valign', 'style', 'variant', 'theme', 'preset',
  ]);

  const machinery = (name: string): boolean => {
    const n = String(name).toLowerCase();
    return SKIP_FIELDS.has(n) || /(?:id|ids|at)$/.test(n);
  };

  /* Where a title comes from, in the order the app itself uses one. */
  const TITLE_FIELDS = ['title', 'name', 'label', 'text', 'heading', 'summary'];

  /* Long enough to be a sentence and short enough that one enormous board scene
     cannot crowd the index. Words past this are still IN the document — they
     are simply not what it is found by. */
  const MAX_TEXT = 8 * 1024;
  const MAX_TITLE = 200;
  const MAX_DEPTH = 12;

  /* A string that is plainly an address rather than a word: no spaces, and the
     alphabet the app mints ids from. Checked as a last resort, because it can
     only ever be a guess — which is why the field-name rule above does the real
     work and this catches ids sitting loose in an array. */
  const idish = (s: string): boolean =>
    (s.length >= 6 && s.length <= 40 && /^[a-z0-9]+$/.test(s) && /\d/.test(s))
    || /^\d{4}-\d{2}-\d{2}/.test(s)
    || /^#[0-9a-f]{3,8}$/i.test(s)
    || /^data:/.test(s)
    /* A record ref: `rec:abc123`, `dbrow:def456`, `note:000000`. It is how one
       document points at another and it is never anything a person typed.
       There are two thousand of them on the 5k account, and left in they were
       40% of the index — every one of them a result titled with an address. */
    || /^[a-z]{2,12}:[A-Za-z0-9_-]{3,40}$/.test(s);

  /** The family a key belongs to, or null for a key search has no business in. */
  function kindOf(key: unknown): string | null {
    if (typeof key !== 'string' || !key.startsWith('md:')) return null;
    if (SKIP_KEYS.has(key)) return null;
    if (SKIP_PREFIXES.some((p) => key.startsWith(p))) return null;
    const rest = key.slice(3);
    const at = rest.indexOf(':');
    return at === -1 ? rest : rest.slice(0, at);
  }

  /** Every word in a value, in the order it appears, machinery left out. */
  function harvest(value: unknown, into: string[], depth: number, budget: { left: number }): void {
    if (depth > MAX_DEPTH || budget.left <= 0) return;
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s || idish(s)) return;
      into.push(s);
      budget.left -= s.length + 1;
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) harvest(item, into, depth + 1, budget);
      return;
    }
    if (value && typeof value === 'object') {
      for (const [name, inner] of Object.entries(value as Record<string, unknown>)) {
        if (machinery(name)) continue;
        harvest(inner, into, depth + 1, budget);
      }
    }
    /* Numbers and booleans are deliberately not indexed. A search for "3" that
       returns every document with a revision, a count or a coordinate in it is
       worse than a search for "3" that returns nothing. */
  }

  /* A named field first, and the first words harvested if there is none.
     Most of an account is database rows, and a row keeps its title inside
     `values` behind a property id nobody can predict — so a title-or-nothing
     rule left the majority of every result list unreadable. The first harvested
     string is the first string in the document, which is a row's primary column
     far more often than it is anything else, and a wrong guess costs a result
     that is labelled oddly rather than one that cannot be found. */
  function titleOf(parsed: unknown, words: string[]): string {
    if (typeof parsed === 'string') return parsed.slice(0, MAX_TITLE);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      for (const field of TITLE_FIELDS) {
        const v = obj[field];
        if (typeof v === 'string' && v.trim() && !idish(v.trim())) return v.trim().slice(0, MAX_TITLE);
      }
    }
    return words.length ? String(words[0]).slice(0, MAX_TITLE) : '';
  }

  /** What one document is worth finding, or null if it is not worth finding.
      NEVER THROWS: search rides along on a flush, and the chokepoint promises a
      write always reports. A value this cannot read costs one unsearchable
      document, not a failed save. */
  function searchDoc(key: unknown, raw: unknown): { kind: string; title: string; text: string } | null {
    const kind = kindOf(key);
    if (kind === null || typeof raw !== 'string') return null;
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return null; }
    /* A TOMBSTONED DOCUMENT IS NOT FINDABLE. What ships with ManDana leaves by
       tombstone rather than by deletion — a built-in cannot be removed from an
       index it was never in (md-project.ts, builtinRemoved) — so the document
       outlives the thing, deliberately, and carrying it in search results would
       make the removal cosmetic. Written as a rule about `deleted` rather than
       about projects, so any family that later leaves the same way gets it. */
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      && (parsed as Record<string, unknown>).deleted === true) return null;
    const words: string[] = [];
    harvest(parsed, words, 0, { left: MAX_TEXT });
    if (!words.length) return null;
    return { kind, title: titleOf(parsed, words), text: words.join('\n').slice(0, MAX_TEXT) };
  }

  /* ── what a person's typing becomes ───────────────────────────────────────
     Every one of " * - ( ) : ^ NEAR OR AND NOT is FTS5 syntax, and an
     unescaped one is not a bad result — it is a thrown exception arriving in
     the middle of somebody typing. So every token is quoted, which makes it a
     literal string to FTS5 whatever it contains, and the operators are spelled
     by this function alone.

     The LAST token gets a prefix star and the others do not: a person typing
     "read qur" has finished the word "read" and is still typing "quran". */
  function searchQuery(input: unknown): string | null {
    if (typeof input !== 'string') return null;
    const tokens = input.split(/[^\p{L}\p{N}_]+/u).filter(Boolean);
    if (!tokens.length) return null;
    return tokens
      .map((t, i) => `"${t}"${i === tokens.length - 1 ? '*' : ''}`)
      .join(' AND ');
  }

  Object.assign(g, {
    mdSearchKind: kindOf,
    mdSearchDoc: searchDoc,
    mdSearchQuery: searchQuery,
  });
})(typeof window !== 'undefined' ? window : globalThis);
